import { useEffect, useMemo, useState } from 'react'
import useConfig from './useConfig'
import { useTranslation } from 'react-i18next'
import useBalances from './useBalances'
import { approveToken, customApproveErc20, ethCallContract } from '../handlers/walletActions/wallet'
import useWallet from './useWallet'
import { keychainManager } from '../keyring'
import getAgent from '../utils/getAgent'
import { useUserTokens } from '../state'
import { useGoogleAnalytics } from '../providers/GoogleAnalyticsProvider'
import { GAEventName } from '../interfaces/googleAnalytics'
import {
  BOB_ARB_ID,
  BOB_BASE_ID,
  BOB_ETH_ID,
  BOB_ICP_ID,
  CBBTC_ARB_ID,
  CBBTC_BASE_ID,
  CBBTC_ETH_ID,
  CBBTC_ICP_ID,
  CKBTC_ARB_ID,
  CKBTC_BASE_ID,
  CKBTC_ETH_ID,
  CKBTC_ICP_ID,
  ETH_ID,
  GLDT_ARB_ID,
  GLDT_BASE_ID,
  GLDT_ETH_ID,
  GLDT_ICP_ID,
  ICP_ARB_ID,
  ICP_BASE_ID,
  ICP_ETH_ID,
  ICP_ICP_ID,
  USDC_ARB_ID,
  USDC_BASE_ID,
  USDC_ETH_ID,
  USDC_ICP_ID,
  USDT_ETH_ID,
  USDT_ICP_ID,
} from '../constants/tokenIds'
import { Chain, PlugToken } from '../interfaces/common'
import { getEthProvider } from '../utils/getEthProvider'
import { useErc20ApproveGas, useETHGasFee } from './useGas'
import { decimalsToE8s } from '../utils/validationHelper'
import LedgerScannerClient from '../clients/LedgerScannerClient'
import principalToBytes32 from '../utils/principalToBytes32'
import { ETH_SUPPORTED_CHAINS, calculateTransactionFee, callContractFunction } from '../utils/eth'
import OneSecClient, { ONESEC_MINTER_CANISTER } from '../clients/OneSecClient'
import { TransferFee } from '../idls/onesec/onesec.did'
import { Abi, GetTransactionCountParameters, encodeFunctionData, parseUnits } from 'viem'
import { getETHId } from '../utils/getETHId'

const HELPER_CONTRACT_MAP: Record<string, string> = {
  [USDC_ETH_ID]: '0xAe2351B15cFf68b5863c6690dCA58Dce383bf45A',
  [USDC_BASE_ID]: '0xAe2351B15cFf68b5863c6690dCA58Dce383bf45A',
  [USDC_ARB_ID]: '0xAe2351B15cFf68b5863c6690dCA58Dce383bf45A',
  [USDT_ETH_ID]: '0xc5AC945a0af0768929301A27D6f2a7770995fAeb',
  [CBBTC_ETH_ID]: '0x7744c6a83E4b43921f27d3c94a742bf9cd24c062',
  [CBBTC_BASE_ID]: '0x7744c6a83E4b43921f27d3c94a742bf9cd24c062',
  [CBBTC_ARB_ID]: '0x7744c6a83E4b43921f27d3c94a742bf9cd24c062',
}

const getETHBridgeFee = async (
  amount: string,
  token: PlugToken,
  chain: Chain,
  principal: string,
  ethAddress: string,
  maxFeePerGas: number,
  maxPriorityFeePerGas: number,
  erc20ApproveGas: number | undefined
) => {
  const helperContract = HELPER_CONTRACT_MAP[token.id]
  const principalInBytes = '0x00' + principalToBytes32(principal).slice(2, -2)
  const data = encodeETHData(
    amount,
    principalInBytes,
    token.decimals,
    helperContract ? 'lock1' : 'burn1'
  )

  const result = await callContractFunction(
    helperContract || token.address!,
    data,
    '0',
    ethAddress,
    chain
  )

  const contractCallGasFee = calculateTransactionFee(
    result?.gasLimit || BigInt(40000),
    BigInt(maxFeePerGas),
    BigInt(maxPriorityFeePerGas)
  )

  let approveGasFee = '0'
  if (helperContract && erc20ApproveGas) {
    approveGasFee = calculateTransactionFee(
      BigInt(erc20ApproveGas),
      BigInt(maxFeePerGas),
      BigInt(maxPriorityFeePerGas)
    )
  }

  return {
    contractCall: Number(contractCallGasFee),
    approve: Number(approveGasFee),
    gasLimit: result?.gasLimit,
  }
}

const encodeETHData = (amount: string, data1: string, decimals: number, functionName: string) => {
  const abi: Abi = [
    {
      type: 'function',
      name: functionName,
      inputs: [
        { name: 'amount', type: 'uint256' },
        { name: 'data1', type: 'bytes32' },
      ],
      outputs: [],
      stateMutability: 'nonpayable',
    },
  ]

  const amountBN = parseUnits(amount, decimals)

  if (!data1.startsWith('0x')) {
    data1 = '0x' + data1
  }

  return encodeFunctionData({
    abi,
    functionName,
    args: [amountBN, data1],
  })
}

interface Props {
  tokenId?: string
}

const useBridgeOneSec = ({ tokenId }: Props) => {
  const { tokens, tokenPricesWithChange } = useConfig()
  const [amount, setAmount] = useState('')
  const [toChain, setToChain] = useState<Chain>(Chain.ETH)
  const [bridgeStep, setBridgeStep] = useState<string>()
  const [fromTokens, setFromTokens] = useState<PlugToken[]>()
  const [bridgeInProgress, setBridgeInProgress] = useState(false)
  const [bridgeReviewVisible, setBridgeReviewVisible] = useState(false)
  const [success, setSuccess] = useState(false)
  const [totalFee, setTotalFee] = useState<
    { gasFee: number; commision: number; gasMoney: number; commisionMoney: number } | undefined
  >()
  const [bridgeMode, setBridgeMode] = useState<'fromICP' | 'toICP'>('fromICP')
  const [oneSecClient, setOneSecClient] = useState<OneSecClient>()
  const [fees, setFees] = useState<TransferFee>()
  const [toToken, setToToken] = useState<PlugToken | undefined>()
  const [token, setToken] = useState<PlugToken | undefined>(
    tokens.find((t) => t.id === (tokenId || USDC_ETH_ID)) || undefined
  )

  const { findBalance, refreshBalance } = useBalances()
  const { wallet } = useWallet()
  const { addToken } = useUserTokens()
  const { fireEvent } = useGoogleAnalytics()
  const { t } = useTranslation()
  const {
    maxFeePerGas,
    maxPriorityFeePerGas,
    failed: gasFailed,
  } = useETHGasFee(bridgeMode === 'toICP', token?.chain, undefined, undefined, undefined, true)
  const erc20ApproveGas = useErc20ApproveGas(
    token?.standard === 'ERC20' ? token.address : undefined,
    Math.ceil(Number(amount) * 10 ** (token?.decimals || 0)),
    token?.chain || Chain.ETH,
    HELPER_CONTRACT_MAP[token?.id || 'a'],
    true
  )

  useEffect(() => {
    if (!tokens) return

    const ids =
      bridgeMode === 'toICP'
        ? [
            USDC_ETH_ID,
            USDC_BASE_ID,
            USDC_ARB_ID,
            ICP_ETH_ID,
            ICP_ARB_ID,
            ICP_BASE_ID,
            BOB_ETH_ID,
            BOB_ARB_ID,
            BOB_BASE_ID,
            GLDT_ETH_ID,
            GLDT_ARB_ID,
            GLDT_BASE_ID,
            CKBTC_ETH_ID,
            CKBTC_ARB_ID,
            CKBTC_BASE_ID,
            CBBTC_ETH_ID,
            CBBTC_ARB_ID,
            CBBTC_BASE_ID,
            USDT_ETH_ID,
          ]
        : [
            ICP_ICP_ID,
            BOB_ICP_ID,
            CKBTC_ICP_ID,
            CBBTC_ICP_ID,
            USDT_ICP_ID,
            USDC_ICP_ID,
            GLDT_ICP_ID,
          ]

    const combined = ids
      .map((id) => tokens.find((t) => t.id === id))
      .filter((token): token is PlugToken => token !== undefined)

    setFromTokens(combined)
  }, [tokens, bridgeMode])

  useEffect(() => {
    const createClient = async () => {
      const identity = await keychainManager.getIdentity(wallet.principalId)
      const agent = getAgent({ identity })
      setOneSecClient(new OneSecClient(agent))
    }

    createClient()
  }, [wallet.principalId])

  useEffect(() => {
    setToChain(bridgeMode === 'toICP' ? Chain.ICP : Chain.ETH)

    const token = tokens.find(
      (t) => t.id === (bridgeMode === 'fromICP' ? USDC_ICP_ID : USDC_ETH_ID)
    )
    if (token) {
      setToken(token)
    }
  }, [bridgeMode])

  useEffect(() => {
    const toToken = tokens.find((t) => t.symbol === token?.symbol && t.chain === toChain)
    if (toToken) {
      setToToken(toToken)
    }

    const fetchFees = async () => {
      if (oneSecClient && toToken && token) {
        setFees(
          await oneSecClient.fetchFees(token.symbol, toToken.symbol, token.chain, toToken.chain)
        )
      }
    }

    fetchFees()
  }, [token, toChain])

  useEffect(() => {
    const calculateFee = async () => {
      if (!token || !wallet.ethAddress || !fees) return

      let gasFee = (token.fee || 0) * 10 ** -token.decimals

      if (ETH_SUPPORTED_CHAINS.includes(token.chain)) {
        if (!maxFeePerGas) return
        const gasFees = await getETHBridgeFee(
          amount,
          token,
          token.chain,
          wallet.principalId,
          wallet.ethAddress,
          maxFeePerGas,
          maxPriorityFeePerGas,
          erc20ApproveGas
        )
        gasFee = gasFees.approve + gasFees.contractCall
      }

      const commision = Number(amount) * fees.protocol_fee_in_percent
      const gasPrice = tokenPricesWithChange[token.standard === 'ERC20' ? ETH_ID : token.id]?.price
      const commisionPrice = tokenPricesWithChange[token.id]?.price

      setTotalFee({
        commision,
        gasFee,
        commisionMoney: commisionPrice * commision,
        gasMoney: gasPrice * gasFee,
      })
    }

    calculateFee()
  }, [
    maxFeePerGas,
    maxPriorityFeePerGas,
    erc20ApproveGas,
    token,
    amount,
    wallet.ethAddress,
    wallet.principalId,
  ])

  const provider = useMemo(() => {
    return getEthProvider(token?.chain || Chain.ETH)
  }, [token?.chain])

  const ethBalance = findBalance({ id: getETHId(token?.chain) })
  const balance = findBalance({ id: token?.id })?.balance || 0

  const receiveAmount = Math.max(Number(amount) - (totalFee?.commision || 0), 0)
  const maxAmount =
    token?.standard === 'ERC20' ? balance : balance - (token?.fee || 0) * 10 ** -token?.decimals!

  const error = useMemo(() => {
    const parsedAmount = Number(amount)

    if (!parsedAmount) return t('onesec.errors.noAmount')
    if (!totalFee || !fees) return t('onesec.errors.fetchingGas')

    if (token?.chain === Chain.ICP) {
      if (parsedAmount > balance) {
        return t('onesec.errors.notEnough', { token: token?.symbol })
      }
    } else {
      if (parsedAmount > balance) {
        return t('onesec.errors.notEnough', { token: token?.symbol })
      }

      if (totalFee.gasFee > (ethBalance?.balance || 0)) {
        return t('onesec.errors.notEnoughForGas', { token: 'ETH' })
      }
    }

    const formattedMinAmount = Number(fees.min_amount) * 10 ** -token?.decimals!
    if (receiveAmount < formattedMinAmount)
      return t('onesec.errors.amountTooSmall', {
        minAmount: formattedMinAmount,
        token: token?.symbol,
      })

    const formattedMaxAmount = Number(fees.max_amount) * 10 ** -token?.decimals!
    if (parsedAmount > formattedMaxAmount)
      return t('onesec.errors.amountTooBig', {
        maxAmount: formattedMaxAmount,
        token: token?.symbol,
      })

    if (gasFailed) return t('common.errors.gasError')
  }, [amount, balance, token, totalFee, gasFailed])

  const bridgeERC20 = async () => {
    if (!token) throw t('onesec.errors.tokenNotFound')
    if (!maxFeePerGas) throw t('onesec.errors.noGasFee')
    if (!wallet.ethAddress || !amount) throw t('onesec.errors.noData')

    const helperContract = HELPER_CONTRACT_MAP[token.id]
    const args = {
      address: wallet.ethAddress,
    }
    const nonce = await provider.getTransactionCount(args as GetTransactionCountParameters)

    if (helperContract) {
      if (!erc20ApproveGas) throw t('onesec.errors.noGasFee')

      setBridgeStep('Approving')

      await customApproveErc20({
        value: decimalsToE8s(amount, token.decimals),
        gasLimit: erc20ApproveGas.toString(),
        maxFeePerGas: maxFeePerGas.toString(),
        maxPriorityFeePerGas: maxPriorityFeePerGas.toString(),
        nonce: nonce.toString(),
        contractAddress: token.address!,
        spender: helperContract,
        chain: token.chain,
      })
    }

    setBridgeStep('Calling contract')

    const principalInBytes = '0x00' + principalToBytes32(wallet.principalId).slice(2, -2)
    const data = encodeETHData(
      amount,
      principalInBytes,
      token.decimals,
      helperContract ? 'lock1' : 'burn1'
    )

    await new Promise((resolve) => setTimeout(resolve, 2000)) // wait 2 seconds for viem to catch up

    const result = await callContractFunction(
      helperContract || token.address!,
      data,
      '0',
      wallet.ethAddress,
      token.chain
    )

    if (!result?.gasLimit) throw t('common.errors.gasError')

    await ethCallContract({
      gasLimit: result.gasLimit.toString(),
      maxFeePerGas: maxFeePerGas.toString(),
      maxPriorityFeePerGas: maxPriorityFeePerGas.toString(),
      nonce: helperContract ? (nonce + 1).toString() : nonce.toString(),
      contractAddress: helperContract ? helperContract : token.address!,
      value: '0',
      data,
      chain: token.chain,
    })
  }

  const bridgeICP = async () => {
    if (!token || !token.address || !toToken) throw t('onesec.errors.tokenNotFound')
    if (!wallet.ethAddress || !oneSecClient) throw t('onesec.errors.noData')

    const fromAmount = Number(decimalsToE8s(amount, token.decimals))

    setBridgeStep('Approving')

    await approveToken({
      spender: { address: ONESEC_MINTER_CANISTER },
      tokenCanisterId: token.address,
      standard: token.standard,
      amount: fromAmount.toString(),
    })

    setBridgeStep('Calling contract')

    await oneSecClient.bridgeFromICP(
      fromAmount,
      wallet.ethAddress,
      wallet.principalId,
      token.symbol,
      toToken.symbol,
      toChain
    )
  }

  const performBridge = async () => {
    if (!token || !toToken) return Promise.reject('No token available')

    setBridgeInProgress(true)

    try {
      if (token.standard === 'ERC20') {
        await bridgeERC20()
      } else {
        await bridgeICP()
      }

      fireEvent(GAEventName.BRIDGE_ONESEC, { amount: Number(amount), token: token.id })
      setSuccess(true)
    } finally {
      addToken(wallet.principalId, toToken.id)
      setBridgeInProgress(false)

      setTimeout(() => {
        LedgerScannerClient.refreshTransactions(wallet)
        refreshBalance(token)
        refreshBalance(toToken)
      }, 3000)
    }
  }

  return {
    amount,
    setAmount,
    maxAmount,
    error,
    performBridge,
    bridgeInProgress,
    bridgeMode,
    setBridgeMode,
    token,
    setToken,
    receiveAmount,
    success,
    fee: totalFee,
    ethBalance,
    bridgeStep,
    bridgeReviewVisible,
    setBridgeReviewVisible,
    toToken,
    toChain,
    setToChain,
    fromTokens,
  }
}

export default useBridgeOneSec
